// Tiny dependency-free SPA router. The app reflects its current view + selected
// project in the URL path so a hard reload restores the page (instead of
// always bouncing back to the chat dashboard).
//
//   /                      -> chat (no project / default)
//   /chat/<projectId>      -> chat with a specific project selected
//   /builds/<projectId>    -> builds page for a project
//   /qa/<projectId>        -> Emil page for a project
//   /review/<projectId>    -> Code Review queue page for a project
//   /admin                 -> admin users page
//   /invite                -> redeem a one-time invite link (unauthenticated)
//
// Unknown paths fall back to chat so a stale or hand-edited URL never strands
// the user on a blank screen.
//
// `/invite` carries its token in the URL fragment, never a path segment, so it
// takes no project id and never round-trips one. A fragment is not sent to any
// server, which keeps the token out of access logs and Referer headers.

export type AppView = "chat" | "admin" | "builds" | "qa" | "review" | "invite";

export interface ParsedRoute {
  view: AppView;
  projectId: string | null;
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/** Parse the SPA route from a URL pathname. */
export function parseRoute(pathname: string): ParsedRoute {
  const parts = pathname.split("/").map((s) => s.trim()).filter(Boolean);
  const section = parts[0] ?? "";
  const projectId = parts[1] ? safeDecode(parts[1]) : null;

  if (section === "builds") return { view: "builds", projectId };
  if (section === "qa") return { view: "qa", projectId };
  if (section === "review") return { view: "review", projectId };
  if (section === "admin") return { view: "admin", projectId: null };
  if (section === "invite") return { view: "invite", projectId: null };
  if (section === "chat") return { view: "chat", projectId };
  return { view: "chat", projectId: null };
}

/** Build the canonical pathname for a view + selected project. */
export function routeToPath(view: AppView, projectId: string | null): string {
  if (view === "builds" && projectId) return `/builds/${encodeURIComponent(projectId)}`;
  if (view === "qa" && projectId) return `/qa/${encodeURIComponent(projectId)}`;
  if (view === "review" && projectId) return `/review/${encodeURIComponent(projectId)}`;
  if (view === "admin") return "/admin";
  if (view === "invite") return "/invite";
  if (view === "chat" && projectId) return `/chat/${encodeURIComponent(projectId)}`;
  return "/";
}
